import { Account, Conviction, ConvictionType } from '@ndvr/portfolio-api';

import { AssetInfo, Position, UniverseExcludedPosition } from '@/cip/_openapi/portfolio-optimizer';
import { toBrokerAccountId } from '@/cip/api/broker-account-id';
import { formatCurrency, formatNumber, formatPercent } from 'utils';

export type ExclusionType = 'Do Not Trade' | 'Do Not Hold' | 'Custom Target';

export const describeExclusion = (conviction: Conviction): { type: ExclusionType; details: string } => {
    switch (conviction.convictionType) {
        case ConvictionType.EXCLUDED:
            return { type: 'Do Not Trade', details: '—' };
        case ConvictionType.QUANTITY:
            return conviction.quantity === 0
                ? { type: 'Do Not Hold', details: '—' }
                : { type: 'Custom Target', details: `Hold ${formatNumber(conviction.quantity)} shares` };
        case ConvictionType.EXPOSURE:
            return { type: 'Custom Target', details: `Hold ${formatCurrency(conviction.exposure)}` };
        case ConvictionType.WEIGHT:
            return { type: 'Custom Target', details: `Hold ${formatPercent(Number(conviction.weight) * 100)}` };
    }
};

export interface PolicyExclusionRow {
    key: string;
    accountGUID: string;
    accountName: string;
    securityId: string;
    symbol?: string;
    description: string;
    type: ExclusionType;
    details: string;
    currentExposure?: number;
}

export const buildPolicyExclusions = (
    accounts: Account[],
    assets: Map<string, AssetInfo>,
    positions: Position[] | undefined,
    unsupported: UniverseExcludedPosition[],
): PolicyExclusionRow[] => {
    const rows: PolicyExclusionRow[] = [];
    for (const account of accounts) {
        const unsupportedPositions = unsupported.filter((item) => item.brokerAccountId === account.brokerAccountId);
        const unsupportedIds = new Set(unsupportedPositions.map((item) => item.sId));
        const holdings = (positions ?? []).filter(
            ({ base }) => toBrokerAccountId(base.brokerId, base.aC) === account.brokerAccountId,
        );
        (account.convictions ?? []).forEach((conviction, index) => {
            // The universe restriction takes precedence over a configurable conviction.
            if (unsupportedIds.has(conviction.securityId)) return;
            const held = holdings.filter(
                ({ base }) =>
                    base.sId === conviction.securityId || (!!base.scId && base.scId === conviction.shareClassId),
            );
            const asset = assets.get(conviction.securityId);
            rows.push({
                key: `${account.accountGUID}:conviction:${index}`,
                accountGUID: account.accountGUID,
                accountName: account.accountName.plainText,
                securityId: conviction.securityId,
                symbol: asset?.symbol ?? held[0]?.base.symbol,
                description: asset?.description ?? held[0]?.base.description ?? conviction.securityId,
                ...describeExclusion(conviction),
                currentExposure: positions
                    ? held.reduce(
                          (sum, item) => sum + item.base.netExposure + (item.calculated?.accruedInterest ?? 0),
                          0,
                      )
                    : undefined,
            });
        });
        unsupportedPositions.forEach((position) =>
            rows.push({
                key: `${account.accountGUID}:unsupported:${position.sId}`,
                accountGUID: account.accountGUID,
                accountName: account.accountName.plainText,
                securityId: position.sId,
                symbol: position.symbol,
                description: position.description,
                type: 'Do Not Trade',
                details: 'Not supported',
                currentExposure: position.currentExposure,
            }),
        );
    }
    return rows;
};

export const summarizePolicyExclusions = (
    rows: PolicyExclusionRow[],
    accounts: Pick<Account, 'accountGUID'>[],
    profileAccounts: { accountGUID: string; aum?: string }[],
    accountGUID: string,
): { total?: number; portfolioPct?: number; accountPct?: number } => {
    const known = rows.filter(
        (row): row is PolicyExclusionRow & { currentExposure: number } => row.currentExposure !== undefined,
    );
    if (known.length < rows.length) return {};
    const aumByAccount = new Map(
        profileAccounts.map((account) => {
            const amount = account.aum?.trim() ? Number(account.aum) : undefined;
            return [
                account.accountGUID,
                amount !== undefined && Number.isFinite(amount) && amount >= 0 ? amount : undefined,
            ];
        }),
    );
    const total = known.reduce((sum, row) => sum + row.currentExposure, 0);
    const accountTotal = known
        .filter((row) => row.accountGUID === accountGUID)
        .reduce((sum, row) => sum + row.currentExposure, 0);
    let portfolioAum: number | undefined = accounts.length ? 0 : undefined;
    for (const account of accounts) {
        const aum = aumByAccount.get(account.accountGUID);
        if (aum === undefined) {
            portfolioAum = undefined;
            break;
        }
        portfolioAum = (portfolioAum ?? 0) + aum;
    }
    const percent = (amount: number, aum?: number) => (aum !== undefined && aum > 0 ? (amount / aum) * 100 : undefined);
    return {
        total,
        portfolioPct: percent(total, portfolioAum),
        accountPct: accounts.some((account) => account.accountGUID === accountGUID)
            ? percent(accountTotal, aumByAccount.get(accountGUID))
            : undefined,
    };
};
